// Drafts bridge.
//
// Two storage backings, picked by the filePath shape:
//   - `local:<uuid>` filePaths → localStorage (free / signed-out)
//   - cloud filePaths → cloud REST (`cloud:*` family handles this
//     directly; this module never sees them on the cloud path)
//
// The old web_scripts/web_drafts Postgres model is gone (those
// migrations were dropped). Free-tier users get an in-browser draft
// list — the names persist across reloads, but content isn't yet
// per-draft (single Y.Doc per local script). Full per-draft Y.Docs
// keyed in y-indexeddb would be a follow-up.

const LS_KEY_PREFIX = 'lastslate:local-drafts:';
const DEFAULT_DRAFTS = [{ name: 'main', displayName: 'First Draft', activeDraft: true }];

function readLocalDrafts(scriptId) {
  if (!scriptId) return DEFAULT_DRAFTS;
  try {
    const raw = localStorage.getItem(`${LS_KEY_PREFIX}${scriptId}`);
    if (!raw) return DEFAULT_DRAFTS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.drafts) || parsed.drafts.length === 0) return DEFAULT_DRAFTS;
    return parsed.drafts;
  } catch {
    return DEFAULT_DRAFTS;
  }
}

function writeLocalDrafts(scriptId, drafts) {
  if (!scriptId) return;
  try {
    localStorage.setItem(`${LS_KEY_PREFIX}${scriptId}`, JSON.stringify({ drafts }));
  } catch {
    /* quota / private-mode — swallow */
  }
}

function pickActiveDraft(drafts) {
  return drafts.find((d) => d.activeDraft) || drafts[0];
}

function isLocalScript(scriptId) {
  return (
    typeof scriptId === 'string' &&
    (scriptId.startsWith('local:') ||
      scriptId.startsWith('import:') ||
      scriptId.startsWith('fixture:'))
  );
}

export function buildDraftsHandlers(/* { supabase } */) {
  return {
    async currentDraft({ filePath: scriptId } = {}) {
      if (!scriptId || isLocalScript(scriptId)) {
        const drafts = readLocalDrafts(scriptId);
        const active = pickActiveDraft(drafts);
        return { ok: true, name: active.name, displayName: active.displayName };
      }
      // Cloud scripts: the renderer reads currentDraft from
      // state.script.cloudDraftId directly; this is just a fallback.
      return { ok: true, name: 'main', displayName: 'First Draft' };
    },

    async listDrafts({ filePath: scriptId } = {}) {
      if (!scriptId || isLocalScript(scriptId)) {
        const drafts = readLocalDrafts(scriptId).map((d) => ({
          name: d.name,
          displayName: d.displayName,
        }));
        return { ok: true, drafts };
      }
      return { ok: true, drafts: [{ name: 'main', displayName: 'First Draft' }] };
    },

    async createDraft({ filePath: scriptId, name, displayName } = {}) {
      if (!scriptId) return { ok: false, error: 'no_script' };
      if (!displayName) return { ok: false, error: 'empty' };
      // Normalise: name is the slug, displayName is what the writer
      // sees. The renderer passes only displayName for local drafts;
      // we derive a slug.
      const slug =
        String(name || displayName)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 40) || `draft-${Date.now().toString(36)}`;
      const dn = String(displayName).trim();
      const drafts = readLocalDrafts(scriptId).map((d) => ({ ...d, activeDraft: false }));
      if (drafts.some((d) => d.name === slug || d.displayName === dn)) {
        return { ok: false, error: 'duplicate' };
      }
      drafts.push({ name: slug, displayName: dn, activeDraft: true });
      writeLocalDrafts(scriptId, drafts);
      return { ok: true, name: slug, displayName: dn };
    },

    async switchDraft({ filePath: scriptId, name } = {}) {
      if (!scriptId || !name) return { ok: false, error: 'scriptId and name required' };
      const drafts = readLocalDrafts(scriptId);
      const idx = drafts.findIndex((d) => d.name === name);
      if (idx < 0) return { ok: false, error: 'not_found' };
      const next = drafts.map((d, i) => ({ ...d, activeDraft: i === idx }));
      writeLocalDrafts(scriptId, next);
      return { ok: true };
    },

    async renameDraft({ filePath: scriptId, name, displayName } = {}) {
      if (!scriptId || !name || !displayName) {
        return { ok: false, error: 'scriptId, name, displayName required' };
      }
      const drafts = readLocalDrafts(scriptId);
      const idx = drafts.findIndex((d) => d.name === name);
      if (idx < 0) return { ok: false, error: 'not_found' };
      drafts[idx] = { ...drafts[idx], displayName: String(displayName).trim() };
      writeLocalDrafts(scriptId, drafts);
      return { ok: true };
    },

    async deleteDraft({ filePath: scriptId, name } = {}) {
      if (!scriptId || !name) return { ok: false, error: 'scriptId and name required' };
      if (name === 'main') return { ok: false, error: 'cannot delete main draft' };
      const drafts = readLocalDrafts(scriptId).filter((d) => d.name !== name);
      // If we deleted the active one, fall back to main.
      if (!drafts.some((d) => d.activeDraft)) {
        const main = drafts.find((d) => d.name === 'main');
        if (main) main.activeDraft = true;
      }
      writeLocalDrafts(scriptId, drafts);
      return { ok: true };
    },

    // Renderer's compare/preview flow calls this. For local-only
    // drafts we don't have per-draft content yet — return empty so
    // the preview surface shows "no content" rather than crashing.
    async readDraftContent(/* { filePath, name } */) {
      return { ok: true, content: '' };
    },
  };
}
